import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import selects from 'scoreboard/components/selects'
import fieldEquationParser from 'scoreboard/datasets/edit/fields/field-equation-parser'
import FieldEquationFormatter from 'scoreboard/datasets/edit/field-equation-formatter'
import formHelper from 'scoreboard/components/form-helper'
import DatasetsEquationForm from 'scoreboard/datasets/equations/datasets-equation-form'
import i18n from 'scoreboard/components/i18n'
import EquationEditor from 'scoreboard/components/equations/equation-editor'
import deviceType from 'scoreboard/components/device-type'
import unwrapModel from 'scoreboard/components/unwrap-model'
import wrapModel from 'scoreboard/components/wrap-model'
import Differ from 'scoreboard/components/differ'
import Dataset from 'scoreboard/datasets/model/dataset'
const { TOTAL, isTotal } = Dataset
import template from './field-equation-form.hbs'
import * as aggregationTypeOptions from 'scoreboard/datasets/aggregation-type-options'
import dropdown from 'scoreboard/components/dropdown'
import datasetPaths from 'scoreboard/datasets/edit/dataset-paths'
import DatasetsFilterBadges from 'scoreboard/datasets/filters/datasets-filter-badges'
import renderFieldDropdown from 'scoreboard/datasets/render-field-dropdown'
import PathToDatasetButton from 'scoreboard/datasets/edit/path-to-dataset-button'
import ObjectSelectorButton from 'scoreboard/components/object-selector-button'
import * as datasetSelectorOptions from 'scoreboard/datasets/dataset-selector-options'
import DataSourceLink from 'scoreboard/components/data-source-selector/data-source-link'
import DataType from 'scoreboard/datasets/model/data-type'
const { DATE, DATASET_LINK } = DataType
import comparisonStrings from '../scorecards/edit/comparison-strings'
import FieldEquationValidator from 'scoreboard/datasets/edit/fields/field-equation-validator'
import constants from 'scoreboard/components/constants'
import totalOption from 'scoreboard/datasets/total-option'
import { LIST_UNIQUE_VALUES } from './model/aggregation-type'
const { DATASET_FIELD_TYPE } = constants
const DatasetFieldType = DATASET_FIELD_TYPE

var FieldEquationForm = DatasetsEquationForm.extend({

	equationParser: fieldEquationParser,
	equationFormatter: FieldEquationFormatter,

	/** @type {Dataset} */
	model: null,
	/**
	 * @type {Object} dataset attributes keyed by numeric ID. NOT wrapped up as
	 * Dataset objects
	 */
	datasetsById: null,
	/**
	 * @type {Object} description of a reference within an equation (these are
	 * rendered in the application as a .badge)
	 */
	sourceDefinition: null,

	events: _.extend({}, DatasetsEquationForm.prototype.events, {
		'change [name=datasetId]': 'onChangeDataset',
		'change [name=fieldId]': 'onChangeField',
		'change [name=columnOrField]': 'onChangeColumnOrField',
		'change [name=aggregationType]': 'onChangeAggregationType',
		'change [name=fieldId], [name=aggregationType]': 'onDone',
		'click .add-filter': 'addFilter'
	}),

	/**
	 * Options for the form used to edit equations using the field syntax (rather than the filter
	 * syntax). These equations may be used for creating calculated fields on the
	 * datasets edit tab or for building calculated columns (#27466)
	 *
	 * @typedef FieldEquationFormOptions
	 *
	 * @property {Dataset} model the Dataset that contains the field or the
	 * starting dataset, depending on where this form is used
	 *
	 * @property {Object} data the object containing the equation and
	 * aggregations that are to be edited. This object is mutated when changes
	 * are made
	 *
	 * @property {Wizard} [wizard] the wizard this form is participating
	 * in, if applicable
	 *
	 * @property {Boolean} [useNativeColumns=false] indicates that for the
	 * native dataset we should use the columns from the source data instead of
	 * the fields
	 *
	 * @property {Number} [startingDatasetId] indicates the ID of the
	 * dataset that should start off our queries, if applicable
	 *
	 * @property {Dataset[]} [datasets] all datasets the user has access to
	 *
	 * @property {Boolean} [allLinkedFields=false] whether to show all
	 * linked fields rather than just the explicitly defined visible linked
	 * fields
	 *
	 * @property {String} [dataType] an optional data type to expect the
	 * evaluated equation to work out to
	 *
	 * @property {Boolean} [individualRecords=false] indicates whether
	 * we're operating on individual records or on aggregated data
	 */

	/**
	 * Form used to edit equations using the field syntax (rather than the filter
	 * syntax). These equations may be used for creating calculated fields on the
	 * datasets edit tab or for building calculated columns (#27466)
	 *
	 * @param {FieldEquationFormOptions} options
	 */
	initialize: function(options = {}) {
		// fields don't have to have equations
		// Use _.clone() here instead of _.cloneDeep() because _.cloneDeep()
		// causes the browser to lock up for a bit when there is a lot of
		// data. Issue #53502
		let opts = _.clone(options)
		opts.required = false
		opts.equation = _.get(opts.data, 'equation')
		// note this.options isn't set until calling
		// DatasetsEquationForm.prototype.initialize
		this.allLinkedFields = _.get(options, 'allLinkedFields',
			false)
		DatasetsEquationForm.prototype.initialize.call(this, opts)
		this.aggregations = _.cloneDeep(_.get(options, 'data.aggregations', []))
		this.useNativeColumns = _.get(options, 'useNativeColumns', false)
		console.dir(this.aggregations)

		/**
		 * @type {Object<Number, Dataset>}
		 */
		this.datasetsById = this.datasetsById || {}
		this.fieldsById = this.model?.combinedFieldsById || {}
		this.visibleLinkedFieldsById = this.visibleLinkedFieldsById || {}
		this.addDatasetsAndLinkedDatasets(options.datasets)
		this.addDatasetsAndLinkedDatasets(
			_.values(_.get(this.model, 'datasetsById')))
		this.sourceDefinition = { filters: [] }
		this.filters = []
		// this.dataset is changed whenever a new dataset is chosen in the
		// dataset selector. This keeps a reference to the original starting
		// dataset
		this.initialModel = this.options.model
		this.treeItemsById = _.cloneDeep(_.get(this.options, 'treeItemsById', {}))
		this.individualRecords = options.individualRecords ?? false
		this.isChangingDataset = false
	},

	/**
	 * Adds the dataset and all linked datasets to the internal caches of datasets
	 * @param {Object[]|Dataset[]} datasets the datasets to add
	 * */
	addDatasetsAndLinkedDatasets: function(datasets = []) {
		datasets = Dataset.wrapDatasetModelsSharingProperties(datasets)
		let datasetsToAdd = _.flatMap(datasets, dataset => {
			const linkedDatasets = dataset?.attributes?.linkedDatasets ?? []
			const allDatasets = [dataset, ...linkedDatasets]
			return _.flatMap(allDatasets, dataset => [
				dataset,
				..._.values(dataset.datasetsById),
				...linkedDatasets
			])
		})
		datasetsToAdd =
			_.filter(datasetsToAdd, dataset => !_.isNil(dataset))
		datasetsToAdd =
			_.uniqBy(datasetsToAdd, dataset => dataset.id)

		const datasetModels =
			Dataset.wrapDatasetModelsSharingProperties(datasetsToAdd)

		let datasetModelsById =
			_.keyBy(datasetModels, 'id')
		_.extend(this.datasetsById, datasetModelsById)
		_.each(datasetModels, dataset => {
			this.addDatasetFields(dataset)
			_.forEach(dataset.linkedDatasets,
					linked => this.addDatasetFields(linked))
		})
	},

	addDatasetFields: function (dataset) {
		const fieldsByIdToAdd =
			_.extend({}, dataset.combinedFieldsById,
			_.keyBy(dataset.get('fields'), 'id'))
		_.extend(this.fieldsById, fieldsByIdToAdd)
		_.extend(this.visibleLinkedFieldsById, _.keyBy(dataset.get('visibleLinkedFields'), 'id'))
	},

	render: function() {
		DatasetsEquationForm.prototype.render.apply(this, arguments)
		this.$('.equation-controls').html(template(this.controlsContext()))

		// after .createEquationEditor is called in the superclass
		this.updateMatchesAndAggregationIndexes()

		this.options.wizard && this.options.wizard.render({
			el: this.el,
			stateSaver: this.saveState.bind(this)
		})

		formHelper.writeToDom(this.options.data, this.el)

		// must go after formHelper.writeToDom
		this.renderChooseSourceColumnOrField()

		// must go after renderChooseSourceColumnOrField
		this.toggleAddEnabled()

		this.disableCutPaste()

		this.handleLinkedDatasets()

		// need to call renderEquation after everything else is rendered so that matches
		// are properly highlighted in the equation editor
		this.equationEditor.renderEquation()

		this.initializeAggregationIndexes()
	},

	disableCutPaste: function() {
		this.equationEditor.registerCutPasteValidator(() => {
			if (this._hasLinkedDataset(this._previousMatches) ||
				this._hasLinkedDataset(this.equationEditor.getMatches())) {
				return 'datasets.equation.field.invalidCutPaste'
			}
		})
	},

	handleLinkedDatasets: function() {
		if (!this.linkedDatasetsSupported() && !_.isEmpty(this.getFromModel('linkedDatasets'))) {
			// display a message that you can't edit linked dataset equations
			let msg = i18n.get('datasets.equation.field.linkedNotSupported')
			this.equationEditor._formView.errorMessage(msg)
			// #18958 button incorrectly disabled in edge
			this.equationEditor._formView.getPrimaryButton().enable()

			// #18958 disable editor for Edge if the equation has linked
			// datasets
			if (this._hasLinkedDataset(this.equationEditor.getMatches())) {
				// remove the ability to edit equations
				this.$('.equation').removeAttr('contenteditable')
				// disable the dataset and column dropdowns
				let dropdowns = this.$('[name=datasetId], [name=columnOrField]')
				dropdowns.prop('disabled', true)
				selects(dropdowns)
				this.$('button.add').prop('disabled', true)
			}
		}
	},

	/**
	 * #57623 initializes the index in the equation editor for the aggregations
	 * for the field that is being edited, so that calls to
	 * {@link #getCurrentMatchAggregation} and {@link #getCurrentMatchAggregationIndex}
	 * will give the correct aggregation.
	 */
	initializeAggregationIndexes: function() {
		// only need to do this for editing a field, since if we're adding a field
		// we'll have the indexes from this.equationEditor
		if (_.isNil(this.options.field)) return

		this._initializeAggregationIndexes(this.options.field.aggregations)
	},

	_initializeAggregationIndexes: function(aggregations = []) {
		const matchesNeedingAggregation =
			this._getMatchesNeedingAggregation()
		this.aggregations = aggregations
		_.forEach(this.aggregations, (agg, index) => {
			// #59981 copy aggregation.id to aggregation.aggregationId to make
			// looking up matches for saved and unsaved aggregations easier
			agg.aggregationId = agg.id ?? agg.aggregationId
			if (_.isNil(agg.aggregationId)) {
				agg.aggregationId = this.getNextUnsavedAggregationId()
			}
			const match = matchesNeedingAggregation[index]
			if (_.isNil(match)) return

			agg.index = match.index
			match.aggregationId = agg.aggregationId
		})
		this.aggregations = _.filter(this.aggregations, (agg, index) => {
			const match = matchesNeedingAggregation[index]
			// if there's no match for this aggregation, remove it
			return !_.isNil(match)
		})

		this.updateAggregationIndexes()
	},

	controlsContext: function() {
		let panelTitle = 'datasets.chooseSource'
		if (!this.showNativeColumns()) {
			panelTitle += '.field'
		}
		return { panelTitle }
	},


	/**
	 * Updates the display without re-rendering the entire view (which would
	 * potentially mess up the visibility of the add button)
	 */
	update: function(autofocus = true) {
		this.renderDatasetChooser()
		formHelper.writeToDom(this.sourceDefinition, this.el)

		this.renderPathToDatasetButton(_.get(this.sourceDefinition, 'path'))
		this.filters = _.get(this.sourceDefinition, 'filters') // #32407
		this.renderFilters()
		this.togglePathToDatasetButton()
		_.invoke(this.fieldDropdown, 'clearError')
		// must go after writeToDom for the pre-selected native column to be present
		// in the dropdown
		this.renderColumnOrField(false, autofocus) // also handles aggregation type
		this.renderDataSourceLink()

		this.updateSourceDefinition()

		return this
	},

	renderDatasetChooser: function() {
		if (this.hasStartingDataset()) {
			this.renderDatasetDropdown()
		} else {
			this.renderDatasetSelectorButton()
		}

		let dataset = this.initialModel || this.model
		let showDatasetControl =
			!_.isEmpty(_.get(unwrapModel(dataset), 'linkedDatasets')) ||
			!this.hasStartingDataset()
		this.$('.dataset').toggleClass('is-not-displayed', !showDatasetControl)
	},

	renderDatasetDropdown: function () {
		this.$('.dataset-selector-button-container').addClass('is-not-displayed')

		let datasets = _(this.getDatasets())
			.map(unwrapModel)
			.filter(dataset => dataset.type === 'dataset')
			.sortBy(dataset => dataset.label.toLowerCase())
			.value()
		this.datasetDropdown = this.$('[name=datasetId]')
		let datasetId = this.getFromModel('rootDatasetId')

		this.datasetDropdown.empty()
		let options = []
		_.each(datasets, dataset => {
			options.push({
				label: dataset.label,
				value: dataset.id,
				selected: dataset.id == datasetId
			})
		})

		selects.filtering(this.datasetDropdown, {
			options,
			placeholder: i18n.get('datasets.choose')
		})
	},

	renderDatasetSelectorButton: async function () {
		this.$('[name=datasetId]').addClass('is-not-displayed')
		this.removeDatasetSelectorButton()


		let dataset = this.getDatasetFromSourceDefinition()
		if (_.isNil(dataset)) {
			await this.loadDataset(this.sourceDefinition?.datasetId)
		}
		dataset = this.getDatasetFromSourceDefinition()

		let selectorOptions = _.extend(
			datasetSelectorOptions.forButton({ dataset }),
			{ message: i18n.get('report.designer.chooseDataset')})

		this.datasetSelectorButton = new ObjectSelectorButton(selectorOptions)

		this.datasetSelectorButton.on('change', async dataset => {
			await this.onChangeDatasetAsync(null, dataset)
		})

		this.$('.dataset-selector-button-container').append(this.datasetSelectorButton.el)
		this.datasetSelectorButton.render()


		const datasetId = selectorOptions?.domainObject?.id ??
			this.sourceDefinition?.datasetId

		// if we have a dataset, no need to load one from the API
		if (!_.isNil(dataset)) return
		// only want to load datasets if we allow any dataset
		if (!this.allowAnyDataset()) return

		await this.loadDataset(datasetId)
	},

	hasStartingDataset: function () {
		return !!this.options.startingDatasetId
	},

	/**
	 * Returns the datasets to be displayed in the dataset dropdown
	 */
	getDatasets: function() {
		let datasets = [ this.model.attributes ]
		if (this.allowAnyDataset()) {
			datasets = _.values(this.options.datasets)
		} else if (this.linkedDatasetsSupported()) {
			let dataset = this.initialModel || this.model
			datasets = _.values(_.get(dataset, 'datasetsById'))
		}
		return datasets
	},

	renderPathToDatasetButtonForDatasetChange: function(newDatasetId) {
		// if we're not changing the dataset, then we can just keep the same
		// path that was already selected
		if (newDatasetId === this.getDatasetId()) return

		this.renderPathToDatasetButton()
	},

	renderPathToDatasetButton: function(path) {
		this.pathToDatasetButton = new PathToDatasetButton({
			model: this.getBaseDataset(),
			path,
			datasetIdRetriever: () => this.getDatasetId()
		})
		this.$('.path-to-dataset-button-container').html(this.pathToDatasetButton.el)
		this.pathToDatasetButton.render()
		this.pathToDatasetButton.on('change:dataset', () => {
			this.onDone()
			this.toggleAddEnabled()
			this.formView.clearErrors()
		})
	},

	/**
	 * Renders the dropdown for choosing the source column or field
	 */
	renderColumnOrField: function(fromDatasetChange = false, autofocus = true) {
		let datasetId = this.getDatasetId() || _.get(this.model, 'id')
		this.setDisabledForColumnOrField(false)
		const dataset = this.datasetsById[datasetId]

		if (this.isShowingOnlyFields()) {
			this.renderOnlyFields(dataset, autofocus, fromDatasetChange)
		} else {
			this.renderFieldsAndColumns(dataset)
		}

		// AFTER drawing the selects
		this.toggleAggregationType()
		this.$('.column').toggleClass('is-not-displayed', this.isShowingOnlyFields())
		this.$('.field-id').toggleClass('is-not-displayed', !this.isShowingOnlyFields())
		// #45767 if no dataset is selected, hide the field dropdown
		if (!datasetId) {
			this.$('.field-id').addClass('is-not-displayed')
		}
	},

	getColumnOrFieldLabelClass: function() {
		return this.isShowingOnlyFields() ? 'field-id' : 'column'
	},

	/**
	 * For the source column or field dropdown, renders only dataset fields and
	 * not source columns. This is appropriate to call for linked datasets. For
	 * the native dataset, renderFieldsAndColumns should be called instead
	 * @param {Dataset} dataset
	 */
	renderOnlyFields: function (dataset, autofocus, fromDatasetChange) {
		if (_.isNil(dataset)) return // #57074

		let selectedId = _.get(this.sourceDefinition, 'fieldId')
		// if there's a dataset selected, but no field, default to the Number of x option
		if (!_.isNil(dataset) && _.isNil(selectedId)) {
			selectedId = TOTAL
		}
		renderFieldDropdown({
			el: this.$('[name=fieldId]'),
			// #43596: We could be rendering because dataset selection was cleared
			// in which case we are not done with selecting the dataset, and should
			// not grab focus
			autofocus: autofocus && !!(fromDatasetChange ? this.getDatasetId() : dataset.id),
			filterVisibleLinkedFields: false,
			dataset,
			allowedFieldIds: _.map(dataset.get('fields'), 'id'),
			addLinkedTotal: true,
			selectedId,
			// // all the fields will be from the same dataset, so the label is
			// // superfluous information
			// showDatasetLabel: false,
			// otherwise changing datasets will always mark the field
			// dropdown invalid which is annoying
			markedFieldOptions: {
				onBlur: _.noop
			}
		})
		this.fieldDropdown = this.$('[name=fieldId]')
		this.renderFieldAggregationType()
	},

	/**
	 * For the source column or field dropdown, renders both fields and
	 * source columns. This is appropriate to call for the native dataset. For
	 * the linked datasets, renderOnlyFields should be called instead
	 * @param {Dataset} dataset
	 */
	renderFieldsAndColumns: function (dataset) {
		const selected = this.getSelectedColumnOrField()
		let fieldOptions = this.getFieldOptions(dataset, selected)
		let columnOptions = this.getColumnOptions(fieldOptions, selected)
		let options = [...columnOptions, ...fieldOptions]
		selects.filtering(this.$('[name=columnOrField]'), {
			options,
			groupBy: 'group'
		})
		this.columnOrFieldDropdown = this.$('[name=columnOrField]')
	},

	/**
	 * Returns the label of the selected column or field from the native dataset
	 */
	getSelectedColumnOrField: function() {
		const sourceDefColumn = _.get(this.sourceDefinition, 'columnOrField')
		let match = this.equationEditor?._matches?.[this.equationEditor?._matchIndex]
		const matchColumn = _.get(match, 'columnOrField')
		// #57865 the match column (selected column in equation editor) should always
		// take precedence over the underlying source definition column state
		return matchColumn ?? sourceDefColumn
	},

	/**
	 * For the source column or field dropdown, returns the options for
	 * source columns in the native dataset
	 */
	getColumnOptions: function (fieldOptions, selectedColumnOrField) {
		const automaticFields =
			this.getFromModel('dataSourceMetadata').fields ?? []
		return FieldEquationForm.getColumnOptions(automaticFields,
			fieldOptions, selectedColumnOrField)
	},

	/**
	 * For the source column or field dropdown, returns the options for fields
	 * in the native dataset
	 */
	getFieldOptions: function (dataset, selectedColumnOrField) {
		let fieldOptions = _(dataset.get('fields'))
			.filter(field => field.id != this.options?.field?.id)
			.filter(field => field.fieldType != 'cluster')
			// #58195 don't show "Record Manually Added" field in the dropdown
			.filter(field => !dataset.isManuallyAddedFieldId(field.id))
			.map(field => {
				return {
					label: field.label,
					value: field.label,
					selected: field.label == selectedColumnOrField,
					group: i18n.get('datasets.field')
				}
			})
			.value()
		return fieldOptions
	},

	/**
	 * @param referenceData { FieldEquationFormReferenceData }
	 */
	getCurrentOrDefaultAggregationType: function(referenceData) {
		const aggregationType = this.$('[name=aggregationType]').val()
		if (!_.isNil(aggregationType)) return aggregationType

		return this.getDefaultAggregationType(referenceData)
	},

	/**
	 * @param referenceData { FieldEquationFormReferenceData }
	 * @returns {string}
	 */
	getDefaultAggregationType: function(referenceData) {
		const columnOrField = referenceData?.column ?? referenceData?.field
		const isCalculatedFromSameDataset =
			FieldEquationValidator.isNonBasicFieldFromSameDataset(this.initialModel, referenceData)
		if (_.isNil(columnOrField) || isCalculatedFromSameDataset) {
			return 'none'
		}

		const fieldId = referenceData?.fieldId
		if (_.isNil(fieldId)) return

		if (isTotal(fieldId)) {
			return 'sum'
		}

		const optionsAndAggType =
			this._getOptionsAndAggType(fieldId)
		return optionsAndAggType?.aggregationType ?? 'none'
	},

	/**
	 * Populates aggregation types appropriate for the selected field
	 */
	renderFieldAggregationType: function() {
		let { fieldId } = this.getCurrentFieldAndFieldId()
		// if the user manually cleared out the field ID, don't worry about
		// re-rendering the aggregation type

		if (_.isNil(fieldId) || fieldId === '') {
			return
		}

		// #46634 if hiding the aggregation type, don't run the rest of this code
		// for native fields otherwise you may use the wrong aggregation because
		// the validation code doesn't account for all the logic that has been added over time see fix
		// of #46476 for one example.  However we DO want to do it fields on the
		// many side of dataset-link since they will always have an aggregation #47158
		if (this.isHideAggregationType() && _.result(this.getBaseDataset(), 'id', null) == this.getDatasetId()) {
			return
		}
		let { options, aggregationType } = this._getOptionsAndAggType(fieldId)

		// #46579 update the aggregation we're editing, if the match has already been
		// added to the equation editor, and there's an aggregation for it
		const currentAggregation = this.getCurrentMatchAggregation()
		if (!_.isNil(currentAggregation)) {
			currentAggregation.aggregationType = aggregationType
		}
		// always update source definition with the new aggregation type
		if (_.isNil(this.sourceDefinition)) {
			this.sourceDefinition = { filters: [] }
		}
		this.sourceDefinition.aggregationType = aggregationType

		dropdown.idLabels(this.$('[name=aggregationType]').empty(),
			options, aggregationType)
	},

	/**
	 *
	 * @param dataset
	 * @return {{aggregationType: String, options: Object[]}}
	 * @private
	 */
	_getOptionsAndAggType: function(fieldId) {
		fieldId = fieldId && parseInt(fieldId)
		const field = this.fieldsById[fieldId]
		let dataType = isTotal(fieldId) ? null : field.dataType
		if (dataType === DATASET_LINK) {
			const field = this.fieldsById[fieldId]
			let linkedDataset = this.datasetsById[field.linkedDatasetId]
			linkedDataset = unwrapModel(linkedDataset)
			const linkedPkField = this.fieldsById[linkedDataset.primaryKeyFieldId]
			fieldId = linkedDataset.primaryKeyFieldId
			dataType = linkedPkField.dataType
		}
		let datasetId
		if (isTotal(fieldId)) {
			datasetId = this.getDatasetId()
		} else {
			datasetId = field.datasetId
		}
		const dataset = this.datasetsById[datasetId]
		let options = aggregationTypeOptions.forField(dataset, fieldId)

		let aggregationType = _.get(this.sourceDefinition, 'aggregationType')
		// if no aggregation type was specified for the many side, None is
		// usually what we want
		if (!this.isOneSide() && _.isUndefined(aggregationType)) {
			aggregationType = 'none'
		}

		// #14089 use default aggregation type
		if (!_.isNil(fieldId) && fieldId != '' && !isTotal(fieldId) && !aggregationType) {
			aggregationType = this.fieldsById[fieldId].aggregationType
		}
		// if we still don't have an aggregation type, try to use the
		// aggregation type that was previously specified
		if (!aggregationType) {
			aggregationType = this.$('[name=aggregationType]').val()
		}

		if (isTotal(fieldId) &&
			aggregationType != 'sum' && aggregationType != 'percentage') {
			aggregationType = 'sum'
		}

		if (this.isOnlyNoneAggregation()) {
			options = [ aggregationTypeOptions.none ]
			aggregationType = 'none'
		}

		// percentages don't make sense for source data
		options = _.filter(options, option => option.id !== 'percentage')
		// #46746 filter out the none aggregation type for datasets that are not
		// in oneDatasetIds on the base dataset
		const oneDatasetIds = this.getFromInitialModel('oneDatasetIds') || []
		const selectedDatasetId = _.get(dataset, 'id')
		if (!_.isNil(this.initialModel) && !oneDatasetIds.includes(selectedDatasetId)) {
			options = _.filter(options, option => option.id !== 'none')
		}

		// if an invalid aggregation type was chosen, select the first one
		let availableAggTypes = _.map(options, 'id')
		if (!_.includes(availableAggTypes, aggregationType)) {
			aggregationType = availableAggTypes[0]
		}

		// #46688 validate that we have a valid option selected after we've initialized
		// all the possible options given the field we're working with
		const selectedOption = _.find(options, option => option.id === aggregationType)
		const isValidAggregationType = !_.isNil(selectedOption)
		// make sure we don't select an invalid aggregation type
		if (dataType === DATE && !isValidAggregationType) {
			aggregationType = 'minimum'
		}

		// for now, don't include "list unique values"
		options = _.filter(options, option => option.id != LIST_UNIQUE_VALUES)

		return { options, aggregationType }
	},

	isFilterValid: function(filter) {
		// filters can be from a different dataset if a starting dataset is specified
		if (this.hasStartingDataset()) {
			return _.defaultTo(filter.valid, true)
		}

		// #45690 if the filter refers to a different dataset than the one we
		// passed in, then it's invalid ...
		return this.model.id == filter.datasetId ||
			// #50859 ... unless the filter's dataset is linked to the passed in
			// dataset
			_.some(this.model.get('linkedDatasets'), linkedDataset =>
				linkedDataset.id == filter.datasetId)
	},

	getFiltersForFilterBadges: function() {
		const filters = _.cloneDeep(this.filters)
		this.updateFilterValidity(filters)
		return filters
	},

	updateFilterValidity: function(filters) {
		_.forEach(filters, filter => {
			filter.valid = this.isFilterValid(filter)
			if (filter.valid) {
				// #50876
				delete filter.filterText
			} else {
				filter.filterText = i18n.get('datasets.filters.error.datasetDifferent')
			}
		})
	},

	renderFilters: function() {
		if (this.isShowingFilters()) {
			_.invoke(this.filterBadges, 'remove')
			this.filterBadges = new DatasetsFilterBadges({
				// #50783 #50829 we need to use this.model instead of
				// this.initialModel. If some of the fields or other data are
				// missing from the model, search in the code for #50673 to see
				// what to do
				model: this.model ?? this.initialModel,
				// #33330 We have to be careful not to pass null or undefined for
				// "filters" because we are counting on sharing an array reference
				// with this view.
				filters: this.getFiltersForFilterBadges(),
				treeItemsById: this.treeItemsById,
				// we want to support relative dates on the Datasets Explore tab to
				// support dynamic columns like SA per 1000 from #27466
				allowAppCalendar: !this.useNativeColumns,
				allLinkedFields: this.allLinkedFields,
				noneMessage: i18n.get('common.thereAreNo', 'report.wizard.filters'),
				datasetsById: this.datasetsById,
				fieldsById: this.fieldsById
			})
			this.filterBadges.on('add-filter', this.onAddFilter.bind(this))
			this.filterBadges.on('edit-filter', this.onEditFilter.bind(this))
			this.filterBadges.on('delete-filter', this.onDeleteFilter.bind(this))
			this.$('.filters.panel-content').html(this.filterBadges.el)
			this.filterBadges.render()
		}
		this.toggleFilters()
	},

	onFilterBadgesEvent: function(treeItems, callback) {
		// #48867 always update tree items first
		_.forEach(treeItems, treeItem => this.treeItemsById[treeItem.id] = treeItem)

		let sourceDefinition = this.getSourceDefinition()
		// if there isn't an equation parts/aggregation, pass an empty object
		// to the callback for add/edit/delete, so we can still update this.filters
		if (_.isNil(sourceDefinition.filters)) {
			sourceDefinition.filters = []
		}

		callback && callback(sourceDefinition)

		this.filters = _.cloneDeep(sourceDefinition.filters)
		this.updateFilterValidity(this.filters) // #50919
		this.formView.change()
		this.onDone()
		this.renderFilters()
	},

	onAddFilter: function(filter, treeItems) {
		this.onFilterBadgesEvent(treeItems, aggregation =>
			aggregation.filters.push(filter))
	},

	onEditFilter: function(newFilter, oldFilter, treeItems) {
		this.onFilterBadgesEvent(treeItems, aggregation => {
			const filterIndex = _.findIndex(aggregation.filters, aggFilter =>
				this.isFilterEqual(aggFilter, oldFilter))
			aggregation.filters[filterIndex] = newFilter

			// #50119 remove the filter we edited from the initialModel/the dataset whose
			// field's we're editing, to avoid duplicate key errors when saving
			if (!_.isNil(newFilter.id) && !_.isNil(this.initialModel)) {
				const newFilters = _.filter(this.initialModel.get('filters'), existingFilter =>
					existingFilter.id !== newFilter.id)
				this.initialModel.filters.set(newFilters)
				this.initialModel.set('filters', newFilters)
			}
		})
	},

	onDeleteFilter: function(filter) {
		this.onFilterBadgesEvent(null, aggregation =>
			_.remove(aggregation.filters, aggFilter =>
				this.isFilterEqual(aggFilter, filter)))
	},

	isFilterEqual: function(filter, filterToCompare) {
		/** #47714 the properties filterText and valid are added in {@link #getFiltersForFilterBadges}
		 * but the filter saved in the DB does not have these properties, so omit them for comparison*/
		// #50875 explicitly omit the properties filterText and valid so that
		// the order of the passed-in filters isn't important
		let omitted = ['filterText', 'valid']
		filter = _.omit(filter, omitted)
		filterToCompare = _.omit(filterToCompare, omitted)
		return _.isEqual(filter, filterToCompare)
	},

	renderDataSourceLink: function() {
		this.dataSourceLink && this.dataSourceLink.remove()
		let datasetId = this.getDatasetId()
		if (!datasetId) {
			return
		}
		this.dataSourceLink = new DataSourceLink({
			section: 'datasets',
			id: datasetId
		})
		this.$('.source-data-link-container').append(this.dataSourceLink.el)
		this.dataSourceLink.render()
	},

	/**
	 * Toggles whether the aggregation type should be visible based on the
	 * currently selected dataset
	 */
	toggleAggregationType: function() {
		let hide = this.isHideAggregationType()
		this.$('label.aggregation-type').toggleClass('is-not-displayed', hide)
		this.$el.toggleClass('has-aggregation-type', !hide)
	},

	togglePathToDatasetButton: function() {
		this.$('.path-to-dataset-button-container')
			.toggleClass('is-not-displayed', !this.shouldShowPathToDataset(this.getDatasetId()))
	},

	shouldShowPathToDataset: function(datasetId) {
		return this.model &&
			this.initialModel &&
			this.initialModel.isAmbiguousPath(datasetId) &&
			!this.allowAnyDataset() &&
			!_.isNaN(datasetId)
	},

	/**
	 * Returns true if we're allowed to reference any dataset, as opposed to
	 * just datasets linked to the main model
	 */
	allowAnyDataset: function() {
		return _.isNil(this.options.startingDatasetId) ||
			'' === this.options.startingDatasetId
	},

	/**
	 * Toggles whether row filters are displayed based on the currently
	 * selected dataset
	 */
	toggleFilters: function() {
		this.$('.filters').toggleClass('is-not-displayed',
			!this.isShowingFilters())
	},

	/**
	 * Non-async version of onChangeDataset. Should only be used during the
	 * #render function, because at that time we will have all the datasets
	 * we need and won't need to load any additional ones
	 */
	onChangeDataset: function(event = {}, dataset,
		fromDatasetChange = true) {
		this.isChangingDataset = true
		const datasetId =
			this._getDatasetIdForOnChangeDataset(dataset)
		this._onChangeDatasetShared(event, datasetId, fromDatasetChange)
	},

	/**
	 * Async version of onChangeDataset. Should be used in most cases, because
	 * we could be changing to a dataset that we don't have in our cache yet
	 * and we will need to load it from the API.
	 */
	onChangeDatasetAsync: async function(event = {}, dataset,
		fromDatasetChange = true) {
		this.isChangingDataset = true
		const datasetId =
			this._getDatasetIdForOnChangeDataset(dataset)

		const cachedDataset = this.datasetsById[datasetId]
		// if the dataset does not exist in our cache, load it
		if (_.isNil(cachedDataset)) {
			await this.loadDataset(datasetId)
		}

		this._onChangeDatasetShared(event, datasetId, fromDatasetChange)
	},

	_getDatasetIdForOnChangeDataset: function(dataset) {
		if (_.isNil(dataset)) {
			dataset = this.datasetsById[this.getDatasetId()]
		}

		let datasetId = _.get(dataset, 'id')
		const value = _.get(event, 'target.value')
		if (_.isNil(datasetId) && !_.isNil(value)) {
			datasetId = parseInt(value, 10)
		}

		return datasetId
	},

	_onChangeDatasetShared: function(event = {}, datasetId, fromDatasetChange = true) {
		const dataset = this.datasetsById[datasetId]

		const initialDatasetId = this.getFromInitialModel('rootDatasetId')
		if (datasetId === initialDatasetId) {
			this.model = this.initialModel
		} else {
			// dataset distinct from the initial dataset
			let differentDataset = dataset
			if (_.isEmpty(differentDataset) && !_.isNil(this.model)) {
				differentDataset = this.model.datasetsById[datasetId]
			}
			if (!_.isEmpty(differentDataset)) {
				this.datasetsById[differentDataset.id] = differentDataset
				this.model = differentDataset
			}
		}
		this.setDisabledForColumnOrField(_.isNil(dataset))
		_.invoke(this.fieldDropdown, 'clearError')
		this.formView.clearErrors()

		if (this.allowAnyDataset()) {
			this.model = dataset
		}

		this.updateFilterValidity(this.filters) // #57076
		this.renderColumnOrField(fromDatasetChange, false)
		this.toggleAggregationType()
		this.toggleFilters()
		this.renderFilters()
		this.renderPathToDatasetButtonForDatasetChange(datasetId)
		this.renderDataSourceLink()
		this.togglePathToDatasetButton()
		this.toggleAddEnabled()
		this.updateSourceDefinition()
		this._editForChangeDataset()
		this.isChangingDataset = false
	},

	_editForChangeDataset: function() {
		if (!this.shouldCallEditForChangeFieldColumnOrDataset()) return

		const currentMatchAggregation = this.getCurrentMatchAggregation()
		if (!_.isNil(currentMatchAggregation)) {
			_.extend(currentMatchAggregation, this.sourceDefinition, currentMatchAggregation)
		}
		this.equationEditor._matches[this.equationEditor._matchIndex] =
			this.sourceDefinition
		this.equationEditor.edit(this.sourceDefinition)

		// have to call edit before changing this.equationEditor._matches so
		// this._previousMatches is set correctly
		this.updateMatchesAndAggregationIndexes()
	},

	loadDataset: async function(datasetId) {
		if (_.isNil(datasetId)) return

		// TODO: add loading dots
		const dataset = await $.ajax({ url: this._getDatasetUrl(datasetId) })
		const datasetModel = wrapModel(dataset, Dataset)
		this.addDatasetsAndLinkedDatasets([datasetModel])
		this.datasetsById[dataset.id] = datasetModel
	},

	_getDatasetUrl: function(datasetId) {
		const params = {
			fields: true,
			connections: true,
			linkedDatasets: true
		}
		return `api/datasets/${datasetId}?${$.param(params)}`
	},

	setDisabledForColumnOrField: function(disable) {
		this.$('[name=fieldId]').attr('disabled', disable)
	},

	/**
	 * @returns {boolean} true if the {@link #equationEditor#edit} function should
	 * be called and the current sourceDefinition/aggregation should be updated
	 * false otherwise.
	 */
	shouldCallEditForChangeFieldColumnOrDataset: function(
		afterReferenceDone = () => {}) {
		this.equationEditor?.referenceDone()

		afterReferenceDone()

		return !_.isNil(this.getSelectedMatch())
	},

	shouldCallEditForChangeFieldOrColumn: function(isField = false) {
		return this.shouldCallEditForChangeFieldColumnOrDataset(
			() => {
				if (isField) {
					this.renderFieldAggregationType()
				}
			})
	},

	/**
	 * @param reference {FieldEquationFormReferenceData}
	 * @private
	 */
	_editForChangeFieldOrColumn: function(reference) {
		this.equationEditor._matches[this.equationEditor._matchIndex] = reference
		this.equationEditor.edit(reference)

		// have to call edit before changing this.equationEditor._matches so
		// this._previousMatches is set correctly
		this.updateMatchesAndAggregationIndexes()
	},

	onChangeField: function() {
		const fieldId = this.$('[name=fieldId]').val()
		const fieldDefined = !_.isNil(fieldId) && fieldId !== ''

		if (fieldDefined) {
			this.renderFieldAggregationType()
		}
		this.toggleAggregationType()

		if (!this.shouldCallEditForChangeFieldOrColumn(true) ||
			!fieldDefined) return

		const selectedMatch = this.getSelectedMatch()
		const reference = this.getReference(
			_.extend({}, selectedMatch, {
			fieldId,
			datasetId: this.getDatasetId()
		}))
		this._editForChangeFieldOrColumn(reference)
	},

	onChangeColumnOrField: function() {
		if (!this.shouldCallEditForChangeFieldOrColumn()) return

		const columnOrField = this.$('[name=columnOrField]').val()
		const reference = this.getReference({
			columnOrField,
			datasetId: this.getDatasetId()
		})
		this._editForChangeFieldOrColumn(reference)
	},

	onChangeAggregationType: function() {
		// don't want to change the aggregation type if there's nothing currently selected
		if (_.isNil(this.getSelectedMatch())) return

		this.sourceDefinition.aggregationType = this.$('[name=aggregationType]').val()
		this.updateSourceDefinition()
	},

	// if the field comes from a dataset that is on the "one" side of a
	// one-to-many relationship (either directly or transitively), it
	// doesn't make sense to do any aggregation
	isOnlyNoneAggregation: function() {
		if (this.allowAnyDataset()) return false

		let { fieldId } = this.getCurrentFieldAndFieldId()
		return this.isOneSide() && fieldId != TOTAL
	},

	/**
	 * Determines whether the aggregation type dropdown should be hidden
	 */
	isHideAggregationType: function() {
		let {fieldId, field} = this.getCurrentFieldAndFieldId()
		// #46782 "Number of <tracked>" fields don't require the user to pick an aggregation
		// type because they are always 'sum'
		if (isTotal(fieldId)) return true

		if (_.isNil(field)) return true

		return !this.needsSpecifiedAggregationType(fieldId)
	},

	/**
	 * Determines whether the currently selected field needs an aggregation type.
	 * Note that this is different from {@link #isHideAggregationType} because that
	 * function just determines what the UI shows, but this determines what needs
	 * to be sent to the server. i.e. "Number of <tracked>" fields still need the
	 * aggregation type 'sum' to be sent to the server, but do not need the user
	 * to pick an aggregation type. See #46782
	 */
	needsSpecifiedAggregationType: function(fieldId) {
		const field = this.fieldsById[fieldId]
		// #46782 the field will be null for "Number of" fields, but they still
		// require an aggregation type
		const needsField = !isTotal(fieldId)
		if (needsField && _.isNil(field)) return false

		return !this.isOnlyNoneAggregation()
	},

	addFilter: function() {
		return this.filterBadges.addFilter()
	},

	/**
	 * Returns the field and fieldId currently selected if any
	 */
	getCurrentFieldAndFieldId: function() {
		let fieldId = this.$('[name=fieldId]').val()
		return {fieldId, field: this.fieldsById[fieldId]}
	},

	/**
	 * Returns the ID of the currently selected dataset
	 */
	getDatasetId: function() {
		const selectedDatasetId = _.isNil(this.datasetSelectorButton) ? null :
			_.get(this.datasetSelectorButton.val(), 'id')
		return this.hasStartingDataset()
			? parseInt(this.$('[name=datasetId]').val())
			: selectedDatasetId
	},

	/**
	 * Returns the dataset referred to by the given sourceDefinition
	 */
	getDatasetFromSourceDefinition: function() {
		let path = _.get(this.sourceDefinition, 'path')
		let datasetId
		if (_.isEmpty(path)) {
			datasetId = _.get(this.sourceDefinition, 'datasetId') ||
				this.getFromModel('rootDatasetId')
		} else {
			datasetId = datasetPaths.lastDatasetId(path)
		}
		return this.datasetsById[datasetId]
	},

	getFromModel: function(attribute) {
		return _.get(unwrapModel(this.model), attribute)
	},

	getFromInitialModel: function(attribute) {
		return _.get(unwrapModel(this.initialModel), attribute)
	},

	getBaseDataset: function() {
		return !_.isNil(this.initialModel) ? this.initialModel : this.model
	},

	/**
	 * @returns {String} the full path to a linked dataset
	 */
	getPath: function() {
		// #33329 if the Path to Dataset button is blank, we shouldn't be
		// pulling the path from it
		if (this.pathToDatasetButton?.$el?.is(':visible')) {
			return this.pathToDatasetButton.path
		} else {
			return null
		}
	},

	/**
	 * Returns whether the currently selected dataset is the dataset of the field we
	 * are currently editing.
	 */
	isSameDataset: function(datasetId = this.getDatasetId()) {
		return this.getRootDatasetIdFromInitialModel() == datasetId
	},

	/**
	 * Returns true if the referenceData needs an aggregation present for it.
	 * @param referenceData { FieldEquationFormReferenceData }
	 * @returns {boolean}
	 */
	needsAggregation: function(referenceData = {}) {
		return FieldEquationValidator.needsAggregation(this.initialModel, referenceData)
	},

	/**
	 * Returns whether the form should only show fields (as opposed to showing
	 * columns as well)
	 */
	isShowingOnlyFields: function() {
		return !(this.isSameDataset() && this.showNativeColumns())
	},

	showNativeColumns: function() {
		return this.editingInitialDataset() && this.useNativeColumns
	},

	editingInitialDataset: function() {
		if (_.isNil(this.initialModel)) return false

		return this.initialModel.id === this.model.id
	},

	/**
	 * Returns whether filters should be displayed for the current dataset
	 */
	isShowingFilters: function() {
		// don't show filters if the user hasn't picked a dataset yet
		if (_.isNil(this.model)) return false
		let showingFilters =
			// when data is being aggregated, we can show filters
			// https://github.com/SpiderStrategies/Scoreboard/issues/49999#issuecomment-1268722340
			!this.individualRecords ||
			// #49914 if there's no starting dataset, we get to use filters
			_.isNil(this.options.startingDatasetId) ||
			// it doesn't make sense to show filters on the "one" side of the
			// relationship
			// https://github.com/SpiderStrategies/Scoreboard/issues/23496#issuecomment-481748651
			// https://github.com/SpiderStrategies/Scoreboard/issues/47782#issuecomment-1252861935
			!this.isOneSide()
		return showingFilters
	},

	/**
	 * Returns whether the dataset is on the "one" side of a one-to-many
	 * relationship (either directly or transitively)
	 */
	isOneSide: function() {
		// #58891 if there's no starting dataset, we're on the "one" side
		// because the reference we're editing is our starting point
		if (_.isNil(this.getBaseDataset())) {
			return true
		}
		return this.getBaseDataset().isOneSide(this.getDatasetId())
	},

	/**
	 * Returns the source definition (a.k.a. the equation part badge)
	 * of the currently selected part of the equation part in the equation editor
	 */
	getSourceDefinition: function() {
		// #18499 need to make sure we keep the ID if it's an existing sourceDefinition
		const fromDom = formHelper.readFromDom(this.el)
		let sourceDefinition = _.extend({},
			this.sourceDefinition, fromDom)
		// #61860 if we have a fieldId from the dom, then that takes precedence
		if (!_.isNil(fromDom?.fieldId)) {
			const fieldIdInt = parseInt(fromDom.fieldId, 10)
			const fieldId = _.isNaN(fieldIdInt) ? null : fieldIdInt
			sourceDefinition.fieldId =  fieldId ?? fromDom.fieldId ??
				this.sourceDefinition.fieldId
			if (!_.isNil(fieldId)) {
				sourceDefinition.field = null
				sourceDefinition.columnOrField = null
			}
		}

		sourceDefinition.datasetId = this.getDatasetId()
		sourceDefinition = this.setDefaultColumnReferenceIfNeeded(sourceDefinition)
		sourceDefinition.path = this.getPath()
		const reference = this.getReference(sourceDefinition)
		_.extend(sourceDefinition, reference)
		return sourceDefinition
	},

	/**
	 * Returns whether the reference currently being edited is fully specified
	 * and ready to be added to the equation
	 */
	isReferenceFullySpecified: function(sourceDefinition) {
		if (this.allowAnyDataset() && !this.showNativeColumns()) {
			const dataset = this.datasetsById[sourceDefinition?.datasetId]
			const fields = dataset?.get('fields')
			return !_.isNil(fields) && sourceDefinition?.fieldId
		}
		const fieldId = parseInt(sourceDefinition.fieldId, 10)
		let specified =
			this.isShowingOnlyFields() && !_.isNil(fieldId)
		if (!this.isShowingOnlyFields()) {
			specified = specified || (!_.isEmpty(sourceDefinition.column) ||
				!_.isEmpty(sourceDefinition.field))
		}
		if (this.model.isAmbiguousPath(this.getDatasetId())) {
			specified = specified && !_.isEmpty(this.getPath())
		}
		if (this.needsSpecifiedAggregationType(sourceDefinition.fieldId)) {
			specified = specified && !_.isNil(sourceDefinition.aggregationType)
		}
		return specified
	},

	/**
	 * Callback executed when changes are performed and the controls are all in
	 * a valid state
	 */
	onDone: function() {
		// must be before calling this.getSourceIndex()
		this.equationEditor.referenceDone()

		this.updateSourceDefinition()
		this.updateAggregationIndexes()
		const currentAggregation = this.getCurrentMatchAggregation()
		if (_.isNil(currentAggregation)) return

		_.extend(currentAggregation, this.sourceDefinition)
	},

	updateSourceDefinition: function() {
		if (_.isNil(this.sourceDefinition)) {
			this.sourceDefinition = { filters: [] }
		}

		let updatedSourceDefinition = this.getSourceDefinition()

		const needsAggregation = this.needsAggregation(updatedSourceDefinition)
		if (needsAggregation) {
			delete updatedSourceDefinition.column
		} else {
			delete updatedSourceDefinition.fieldId
		}
		// checking this way to piggyback on the logic in toggleFilters
		if (!this.$('.filters').eq(0).hasClass('is-not-displayed')) {
			updatedSourceDefinition.filters = _.cloneDeep(this.filters)
		}

		// set a default value for when the user doesn't have to pick the aggregation type
		const needsAggregationType =
			this.needsSpecifiedAggregationType(updatedSourceDefinition.fieldId)
		if (this.isHideAggregationType() && needsAggregationType) {
			updatedSourceDefinition.aggregationType =
				this.getCurrentOrDefaultAggregationType(updatedSourceDefinition)
		}
		// if we don't need an aggregation type, then delete the one that's there
		if (!needsAggregationType || _.isNil(updatedSourceDefinition.aggregationType)) {
			updatedSourceDefinition.aggregationType = 'none'
		}

		this.sourceDefinition = updatedSourceDefinition
	},

	_hasLinkedDataset: function(matches) {
		return !!_.find(matches, match => !_.isNil(match.dataset))
	},

	createEquationEditor: function() {
		DatasetsEquationForm.prototype.createEquationEditor.apply(this, arguments)
		this.equationEditor._formView.on('change', this.toggleAddEnabled.bind(this))
		this.equationEditor._referenceData = el => this._referenceData(el)
		this.equationEditor.getMatches = e => this.getMatches(e)
		this.equationEditor.add = e => this.add(e)
		this.equationEditor.edit = e => this.equationEditorEdit(e)
		this.equationEditor.on('selection', this.selection.bind(this))
		this.equationEditor.clickEquation = e => this.clickEquation(e)
		this.equationEditor.removeSelected = e => this.removeSelected(e)
		/* #18397 the intention here was to clear the banner-error after typing
		something, but I couldn't get it to work properly
		this.equationEditor.typeEquation = function(e) {
			EquationEditor.prototype.typeEquation.apply(this, arguments)
			if (e.type != 'paste' && e.type != 'cut') {
				this._formView.clearErrors()
			}
		}
		*/
		// needed because events were bound incorrectly before the new add
		// method was defined
		this.equationEditor.undelegateEvents()
		this.equationEditor.delegateEvents()
	},

	_referenceData: function(el, data = {}) {
		let dataFromEquationEditor =
			EquationEditor.prototype._referenceData.call(this.equationEditor, el)
		dataFromEquationEditor = this.cleanReferenceData(dataFromEquationEditor)
		data = _.extend({}, data, dataFromEquationEditor)
		data = this.getReference(data)
		data.filters = _.cloneDeep(this.filters)
		data = this.setDefaultColumnReferenceIfNeeded(data)
		return data
	},

	setDefaultColumnReferenceIfNeeded: function(referenceData) {
		// if we're not currently changing the dataset, then we don't want to
		// default to anything
		if (!this.isChangingDataset) return referenceData

		const changingToBaseDataset =
			this.isSameDataset(referenceData.datasetId)
		const propsToKeep = ['datasetId', 'index', 'aggregationId', 'filters']
		if (changingToBaseDataset) {
			if (!_.isNil(referenceData.columnOrField) &&
				!_.isEmpty(referenceData.columnOrField)) return referenceData

			// when changing the dataset from a linked dataset, back to the base
			// dataset, we don't have Number of x as an option, so we must default
			// to the first column
			referenceData = _.pick(referenceData,propsToKeep)
			const dataset = this.datasetsById[referenceData.datasetId]
			referenceData.dataset = dataset.get('label')
			referenceData.columnOrField =
				dataset.get('dataSourceMetadata')?.columns?.[0]
		} else {
			if (!_.isNil(referenceData.fieldId) &&
				!_.isEmpty(referenceData.fieldId)) return referenceData

			// when changing the dataset from the base dataset to a linked dataset,
			// or in the case of data-source-field-equation-form.js changing
			// to any other dataset, default to the "Number of <tracked>" field
			referenceData = _.pick(referenceData, propsToKeep)
			referenceData.fieldId = TOTAL
		}

		return referenceData
	},

	getMatches: function() {
		let matches = EquationEditor.prototype.getMatches.apply(this.equationEditor, arguments)
		matches = _.map(matches, match => this.getReference(match))
		this.updateAggregationIdsOnMatches(matches)
		return matches
	},

	/**
	 * Updates the aggregationIds on this.equationEditor._matches that
	 * will require an aggregation. This is necessary because we need to link
	 * the matches in the equation editor back to their underlying aggregation
	 * as the equation is edited by the user.
	 * @param matches {Array<FieldEquationFormReferenceData|Object>} - the matches to update
	 */
	updateAggregationIdsOnMatches: function(matches = this.equationEditor._matches) {
		const matchesNeedingAggregationByIndex =
			this._getMatchesNeedingAggregationByIndex(matches)
		const matchesNeedingAggregation = _.values(matchesNeedingAggregationByIndex)
		const aggregationsByAggregationId =
			_.keyBy(this.aggregations, 'aggregationId')
		// #59981 assign ids to the matches that need aggregation. Either use
		// the aggregation's ID that's stored in the db or assign the next negative number
		_.forEach(matchesNeedingAggregationByIndex, (match, index) => {
			// want to make sure we're working with the reference data that has
			// datasetId and fieldId for when we use this.isMatchEqualToAggregation
			match = this.getReference(match)
			index = parseInt(index, 10)
			// make sure the changes here are updated in the matches since
			// thi.getReference uses _.clone
			matches[index] = match
			// also update the matches we're iterating over for when we generate
			// new aggregationIds
			matchesNeedingAggregation[index] = match

			// if we already have an aggregationId for this match that corresponds
			// to an aggregation, don't change anything
			if (!_.isNil(match.aggregationId) &&
				!_.isNil(aggregationsByAggregationId[match.aggregationId])) return

			let aggregation = this.aggregations[index]
			if (_.isNil(aggregation)) {
				match.aggregationId =
					this.getNextUnsavedAggregationId(matchesNeedingAggregation)
				return
			}

			// we could be in a state where the matches and aggregations aren't in
			// sync yet, such as when the user has just deleted something in
			// the equation editor, so check to see if this match is "equal" to
			// the aggregation
			const isEqual = this.isMatchEqualToAggregation(
				match, aggregation, matches)
			if (!isEqual) {
				// if the match isn't equal to the aggregation, we need to find
				// the matching aggregation
				const aggregationsAfterThisOne = this.aggregations.slice(index)
				const matchingAggregation = _.find(aggregationsAfterThisOne,
						agg => this.isMatchEqualToAggregation(match, agg, matches))
				aggregation = matchingAggregation
			}

			if (_.isNil(aggregation)) {
				match.aggregationId =
					this.getNextUnsavedAggregationId(matchesNeedingAggregation)
				return
			}

			const existingId = aggregation?.id ?? aggregation?.aggregationId
			// if the found aggregation already has an aggregationId, use that
			if (!_.isNil(existingId)) {
				match.aggregationId = existingId
				return
			}

			// if the found aggregation doesn't have an aggregationId, assign
			// the next negative number
			// put this under .aggregationId instead of ID, so we don't accidentally
			// end up saving negative IDs to the database!
			aggregation.aggregationId =
				this.getNextUnsavedAggregationId(matchesNeedingAggregation)
		})
	},

	isMatchEqualToAggregation: function(match, aggregation, matches) {
		const fieldsToCompare = ['fieldId', 'datasetId']
		return _.isEqual(
			_.pick(match, fieldsToCompare),
			_.pick(aggregation, fieldsToCompare))
	},

	/**
	 * Gets the next id to use for unsaved aggregations. Counts down from -1
	 */
	getNextUnsavedAggregationId: function(matchesNeedingAggregation = []) {
		let existingIds =
			_.compact(_.map(this.aggregations, 'aggregationId'))
		// need to take into account the matches that have aggregationIds
		// assigned because we may have added a match but not added an aggregation
		// for it yet!
		existingIds = _.concat(existingIds,
			_.compact(_.map(matchesNeedingAggregation, 'aggregationId')))
		const min =
			_.isNaN(_.min(existingIds)) || _.isNil(_.min(existingIds))
				? 0 : _.min(existingIds)
		return min - 1
	},

	_shouldAddReference: function(e) {
		if (this.$(e.currentTarget).hasClass('is-disabled')) return false // #50556
		let dataset = this.initialModel || this.model
		if (this.options.startingDatasetId &&
			dataset.isAmbiguousPath(this.getDatasetId()) &&
			_.isEmpty(this.getPath())) {
			this.formView.errorMessage(i18n.get('datasets.linked.choosePathToDataset.required'))
			return false
		}

		return true
	},

	add: function(e) {
		if (!this._shouldAddReference(e)) return

		this.updateMatchesAndAggregationIndexes() // #28007
		EquationEditor.prototype.add.apply(this.equationEditor, arguments)
	},

	equationEditorEdit: function(e) {
		EquationEditor.prototype.edit.apply(this.equationEditor, arguments)
		this.equationEditor.toggleAddVisible(true)
		this.equationEditor.isEditingReference = true
		this.updateMatchesAndAggregationIndexes() // #28007
	},

	clickEquation: function(e) {
	 	EquationEditor.prototype.clickEquation.apply(this.equationEditor, [false])
	},

	removeSelected: function(e) {
		const fromDatasetSelector = this.fromDatasetSelector(e)
		const fromColumnOrFieldDropdown = this.fromFieldOrColumnDropdown(e)
		// if the blur event came from clicking the dataset selector button, or
		// the field/column dropdown we don't want to un-select the match in
		// the equation editor, because whatever the user does with those inputs
		// will edit the currently selected match
		if (fromDatasetSelector || fromColumnOrFieldDropdown) return

		EquationEditor.prototype.removeSelected.apply(this.equationEditor, arguments)
		this.updateAggregationIndexes()
	},

	fromDatasetSelector: function(e) {
		if (_.isNil(e?.relatedTarget)) return false

		const fromDatasetSelectorButton = !_.isNil(this.datasetSelectorButton) &&
			e?.relatedTarget === this.datasetSelectorButton.el

		let fromDatasetDropdown = false
		if (!_.isNil(this.datasetDropdown)) {
			const selectElement =
				this._getSelectElementFromRelatedTarget(e, 'datasetId')
			fromDatasetDropdown = this.datasetDropdown.is(selectElement)
		}

		return fromDatasetSelectorButton || fromDatasetDropdown
	},

	fromFieldOrColumnDropdown: function(e) {
		if (_.isNil(e?.relatedTarget)) return false

		let fromColumnOrFieldDropdown = false

		if (!_.isNil(this.columnOrFieldDropdown)) {
			const selectElement =
				this._getSelectElementFromRelatedTarget(e, 'columnOrField')
			fromColumnOrFieldDropdown =
				this.columnOrFieldDropdown.is(selectElement)
		}
		if (!_.isNil(this.fieldDropdown)) {
			const selectElement =
				this._getSelectElementFromRelatedTarget(e, 'fieldId')
			fromColumnOrFieldDropdown =
				fromColumnOrFieldDropdown || this.fieldDropdown.is(selectElement)
		}

		return fromColumnOrFieldDropdown
	},

	_getSelectElementFromRelatedTarget: function(e, name) {
		if (_.isNil(e?.relatedTarget)) return null

		const relatedTarget = this.$(e.relatedTarget)
		const label = relatedTarget.closest('label')
		return this.$(label)?.find(`[name=${name}]`)?.[0]
	},

	/**
	 * @typedef {Object} FieldEquationFormReferenceData
	 * @property {String} [column] - the source column name.
	 * Note, only one of column or field will be present.
	 * @property {String} [field] - the field label.
	 * Note, only one of column or field will be present.
	 * @property {Number} [fieldId] - the ID of the field.
	 * @property {String} [columnOrField] - the source column name or field label.
	 * @property {Number} [datasetId] - the ID of the dataset the field or column is from.
	 * @property {String} [dataset] - the label of the dataset the field or column is from.
	 * 								Note, this will only be present if the field or column
	 * 								is from a linked dataset.
	 */

	/**
	 * Returns the reference data from the given match from the equation editor.
	 * There are 3 main cases:
	 * - A column from the source data of this dataset will have the `column` property.
	 * 	 This also applies to basic fields that reference said column, because they do not
	 * 	 need aggregations specified for their equation (a single source column).
	 * - A calculated field from this dataset will have the `field` property.
	 * - A field from a linked dataset will have the `datasetId`, `dataset`, and
	 * `field` properties.
	 * @param [data] {Object | FieldEquationFormReferenceData} - can be a match from the
	 * equation editor, this.sourceDefinition, or a FieldEquationFormReferenceData object.
	 * @returns {FieldEquationFormReferenceData}
	 */
	getReference: function(data = {}) {
		/** @type {FieldEquationFormReferenceData} */
		let referenceData = _.clone(data)
		referenceData = this.cleanReferenceData(referenceData)

		let referencedDatasetId = !_.isNil(referenceData.datasetId) ?
			parseInt(referenceData.datasetId) : null
		// if the dataset's id isn't specified in the reference already we must
		// have a raw match from the equation editor with just the dataset's name
		if (_.isNil(referencedDatasetId)) {
			const datasets =
				_.map(this.datasetsById, dataset => unwrapModel(dataset))
			const datasetsByLabel =
				_.keyBy(datasets, 'label')
			referencedDatasetId = datasetsByLabel[referenceData.dataset]?.id
		}
		if (_.isNil(referencedDatasetId)) {
			referencedDatasetId = this.initialModel && this.initialModel.id
		}
		const referencedDataset = this.datasetsById[referencedDatasetId]

		// we're dealing with a linked dataset, or are on a form that does not come from
		// editing a particular dataset
		const isSameDataset = this.isSameDataset(referencedDatasetId)
		if (!isSameDataset && !_.isNil(referencedDatasetId)) {
			referenceData = this.getReferenceForDatasetField(referencedDataset, referenceData)
		}

		if (isSameDataset) {
			_.extend(referenceData,
				this.getReferenceForBaseDatasetFieldOrSourceColumn(referenceData))
		}

		this.copyColumnOrFieldInReferenceData(referenceData)

		return this.cleanReferenceData(referenceData)
	},

	/**
	 * @param dataset {Dataset}
	 * @param data {FieldEquationFormReferenceData}
	 * @returns {{field: String, datasetId: *, dataset: any, fieldId: (*|number)}}
	 */
	getReferenceForDatasetField: function(dataset, data) {
		let field = data.field
		let fieldId = data.fieldId
		// try to find the field from the existing field identifier or fieldId
		if (!_.isNil(field)) {
			const fieldsByLabel = Dataset.getFieldsByLabel(dataset)
			const foundField = fieldsByLabel ? fieldsByLabel[field] : null
			fieldId = foundField?.id ?? fieldId
		}
		// if we didn't find the field from the field label, then it must be the
		// "Number of <tracked>" field
		if (_.isNil(fieldId)) {
			const totalLabel = totalOption(dataset)?.label
			const isTotalField = field === totalLabel
			if (isTotalField) {
				fieldId = TOTAL
			}
		}

		fieldId = fieldId && parseInt(fieldId)
		field =
			this.getFieldForReference(dataset, fieldId)
		return _.extend({}, data, {
			datasetId: dataset?.id,
			dataset: _.get(unwrapModel(dataset), 'label'),
			field,
			fieldId
		})
	},

	getReferenceForBaseDatasetFieldOrSourceColumn: function(data) {
		const dataset = this.initialModel
		const referenceData = _.clone(data)
		referenceData.datasetId = dataset.id
		const columnOrField = referenceData.columnOrField ??
			this.$('[name=columnOrField]').val()
		const fieldId = parseInt(data?.fieldId, 10)
		let field = Dataset.getFieldsByLabel(dataset)[columnOrField]
		if (_.isNil(field)) {
			field = dataset.combinedFieldsById[fieldId]
		}
		// #57870 a field can't reference itself!
		const isOtherField = !_.isNil(field) &&
			field.id !== this.options?.field?.id &&
			field.fieldType !== DatasetFieldType.BASIC
		if (isTotal(fieldId)) {
			referenceData.field = this.getFieldForReference(dataset, fieldId)
			referenceData.fieldId = fieldId
		} else if (isOtherField) {
			// #51637 if the field is calculated, we want the reference data to show that
			// it's a field and not a column, but we only want to do this for references that
			// reference calculated fields
			referenceData.field = _.get(unwrapModel(field), 'label')
			referenceData.fieldId = _.get(unwrapModel(field), 'id')
			delete referenceData.column
		} else {
			// we're dealing with a column from the source data
			if (_.isNil(referenceData.column)) {
				referenceData.column = referenceData.columnOrField
			}
		}
		return _.extend({}, data, referenceData)
	},

	/**
	 * @param dataset {Dataset}
	 * @param fieldId {Number}
	 * @return {String} the field label for the given fieldId
	 */
	getFieldForReference(dataset, fieldId) {
		fieldId = parseInt(fieldId, 10)
		let field = this.fieldsById[fieldId]
		if (isTotal(fieldId)) {
			// #58832 `dataset ?? this.model` for places where there's no
			// starting dataset (e.g. chart series)
			field = totalOption(dataset ?? this.model)?.label
		}  else {
			field = _.get(unwrapModel(field), 'label')
		}
		return field
	},

	/**
	 * @param data {FieldEquationFormReferenceData}
	 * @returns {FieldEquationFormReferenceData}
	 */
	cleanReferenceData: function(data) {
		return _.pickBy(data, value =>
			!_.isNil(value) && value !== '')
	},

	/**
	 * The equation editor matches and highlights equations based on
	 * FieldEquationFormReferenceData.columnOrField but in this class we differentiate
	 * between an equation part that is a source column or a dataset field. This method
	 * copies either the FieldEquationFormReferenceData.column or
	 * FieldEquationFormReferenceData.field to FieldEquationFormReferenceData.columnOrField
	 * if it is not already set.
	 * @param data
	 */
	copyColumnOrFieldInReferenceData: function(data) {
		if (_.isNil(data.columnOrField)) {
			if (!_.isNil(data.field)) {
				data.columnOrField = data.field
			} else if (!_.isNil(data.column)) {
				data.columnOrField = data.column
			}
		}
	},

	linkedDatasetsSupported: function() {
		return !deviceType.trident && !deviceType.edge
	},

	/**
	 * Renders a column dropdown or a button used to change the source.
	 */
	renderChooseSourceColumnOrField: function() {
		this.updateAggregationIndexes()
		this.sourceDefinition = this.aggregations[this.currentMatchAggregationIndex]
		this.update()
		const dataset = this.getDatasetForMatch(this.sourceDefinition)
		this.onChangeDataset({}, dataset, false)
	},

	/**
	 * Returns the dataset data corresponding to the given match.
	 * @return {Object} a plain JavaScript object version of a Dataset
	 */
	getDatasetForMatch: function(match) {
		let dataset
		if (match.dataset) {
			// try to find the right dataset by label
			dataset = _.find(_.values(this.datasetsById), function(d) {
				return unwrapModel(d)?.label?.toLowerCase() == match?.dataset?.toLowerCase()
			})
		} else {
			// we must have been referring to the initial dataset
			dataset = this.getRootDatasetFromInitialModel()
		}
		// if we still can't find a dataset, just default to the current one
		if (_.isNil(dataset)) {
			dataset = unwrapModel(this.model)
		}
		return unwrapModel(dataset)
	},

	/**
	 * @returns {Object} plain JS Dataset object
	 */
	getRootDatasetFromInitialModel: function() {
		const rootDataset = this.getFromInitialModel('rootDataset')
		return _.isNil(rootDataset) ? unwrapModel(this.initialModel) : rootDataset
	},

	getRootDatasetIdFromInitialModel: function() {
		return _.get(this.getRootDatasetFromInitialModel(), 'id')
	},

	/**
	 * Handles this.equationEditor.on('edit')
	 *
	 * @param {FieldEquationFormReferenceData} matchOrReference the current match or
	 * reference being edited
	 */
	edit: function(matchOrReference) {
		const needsAggregation = this.needsAggregation(matchOrReference)
		const previousMatch = this._previousMatches[this.equationEditor._matchIndex]
		const previousMatchNeedsAggregation = this.needsAggregation(previousMatch)
		let updatedSourceDefinition = this.getReference(matchOrReference)

		this.updateSourceDefinition()
		updatedSourceDefinition = _.extend({}, this.sourceDefinition,
			updatedSourceDefinition)
		// if we're adding a calculated field from the same dataset or a field from a linked dataset
		if (needsAggregation) {
			updatedSourceDefinition =
				this._editForMatchThatNeedsAggregations(updatedSourceDefinition)
		}


		// if we're editing a column from the source data, or a basic field
		if (!needsAggregation && previousMatchNeedsAggregation) {
			// if the previous match needed an aggregation, then we need to
			// remove the previous match from the aggregations array
			const aggregationIndex = this.previousMatchAggregationIndex
			this.aggregations.splice(aggregationIndex, 1)
		}

		// update the source definition with the aggregation/column we just updated
		// so that when formHelper.writeToDom(this.sourceDefinition) is called
		// in the subsequent calls to this.update that the correct info
		// is displayed for the controls
		this.sourceDefinition = this.getReference(updatedSourceDefinition)
		this.update(false)

		// delete undefined members so we don't pollute our aggregations
		this.aggregations = _.map(this.aggregations, aggregation =>
			_(aggregation).omitBy(_.isNil).value())
		this.updateAggregationIdsOnMatches()

		console.dir('Aggregations:')
		console.dir(this.aggregations)
		console.dir('Equation Editor Matches:')
		console.dir(this.equationEditor._matches)
	},

	/**
	 * @param {FieldEquationFormReferenceData} matchOrReference the current match or
	 * reference being edited
	 */
	_editForMatchThatNeedsAggregations: function(matchOrReference) {
		this.updateAggregationIndexes()
		const currentAggregationIndex = this.currentMatchAggregationIndex
		const currentDataset = this.getDatasetForMatch(matchOrReference)
		let datasetId = _.get(currentDataset, 'id') ||
			this.aggregations[currentAggregationIndex]?.datasetId

		let updatedSourceDefinition = this.getReference(matchOrReference)

		const matchAdded = this.addMatch(updatedSourceDefinition.fieldId, datasetId)
		const matchRemoved = this.removeMatch()
		let matchModified = false
		if (!matchAdded &&  !matchRemoved) {
			matchModified = this.modifyMatch(matchOrReference)
		}
		const aggregationsUpdated = matchAdded || matchRemoved || matchModified

		if (currentAggregationIndex === -1)  return updatedSourceDefinition

		// if we added, removed, or modified a match, update the current source definition
		// to be in sync with the current source index, and set the selected equation part
		// to be the one we just worked on.
		// #37998 if we updated this.aggregations by either adding, removing, or modifying
		// a match, then initialize it by copying the values from the source definition
		// such as path
		if (aggregationsUpdated) {
			const existingAggregation = this.aggregations[currentAggregationIndex]
			// #47226 don't copy the filters
			const sourceDefExceptFilters =
				_.omit(updatedSourceDefinition, 'filters')
			const updatedAggregation = _.extend({}, sourceDefExceptFilters,
				existingAggregation)
			const existingId = _.get(existingAggregation, 'id')
			// #44766 if there was no id from the existing aggregation, then
			// that means we're adding a new one, so delete it to prevent
			// duplicate key exceptions
			if (_.isNil(existingId)) {
				delete updatedAggregation.id
			}
			this.aggregations[currentAggregationIndex] = updatedAggregation
		}

		updatedSourceDefinition = this.aggregations[currentAggregationIndex]

		return updatedSourceDefinition
	},

	addMatch: function(fieldId, datasetId) {
		const equationEditorHasMoreMatches =
			this._previousMatches.length < this.equationEditor._matches.length
		if (_.isNil(fieldId) || !equationEditorHasMoreMatches) return

		this.aggregations.splice(this.currentMatchAggregationIndex, 0, {
			fieldId,
			filters: this.filters,
			datasetId
		})

		return true
	},

	removeMatch: function() {
		const equationEditorHasLessMatches =
			this._previousMatches.length > this.equationEditor._matches.length
		if (!equationEditorHasLessMatches) return

		this.aggregations.splice(this.currentMatchAggregationIndex, 1)
		return true
	},

	/**
	 * @param {FieldEquationFormReferenceData} matchOrReference the current match or
	 * reference being edited
	 */
	modifyMatch: function(matchOrReference) {
		const currentAggregation = this.getCurrentMatchAggregation()
		const aggregationIndex = this.currentMatchAggregationIndex
		const sourceIndexFieldId = _.get(currentAggregation, 'fieldId')
		if (_.isNil(matchOrReference?.fieldId) || aggregationIndex < 0 ||
			sourceIndexFieldId == matchOrReference?.fieldId) return

		if (!_.isNil(currentAggregation)) {
			delete currentAggregation.match
		}
		this.aggregations[aggregationIndex] = _.extend({},
			currentAggregation, matchOrReference)
		return true
	},

	// handles this.equationEditor.on('selection')
	selection: function() {
		this.diffMatchesAndUpdateAggregations()

		if (_.isNil(this.equationEditor._matchIndex)) {
			this.filters = []
			this.renderFilters()
		}

		this.updateMatchesAndAggregationIndexes()

		const currentAggregation = this.getCurrentMatchAggregation()
		if (!_.isNil(currentAggregation)) {
			this.sourceDefinition = _.extend({},
				this.sourceDefinition, currentAggregation)
			this.model = this.datasetsById[currentAggregation.datasetId]
		}

		// don't need to update the dataset/field selectors if the selection
		// is not on a match
		if (_.isNil(this.getSelectedMatch())) return

		this.update(false)
	},

	/**
	 * Updates the aggregations array based on the differences between the
	 * current matches and the previous matches.
	 */
	diffMatchesAndUpdateAggregations: function() {
		// #21858 handle when a range of text is deleted
		const differ = this.getDiffOfMatchesNeedingAggregation()
		if (_.isEmpty(differ.allChanges())) return

		const aggregationsByAggregationId =
			_.keyBy(this.aggregations, 'aggregationId')

		_.forEach(differ.removed, removedMatch => {
			const aggregation =
				aggregationsByAggregationId[removedMatch.aggregationId]
			if (_.isNil(aggregation)) return

			this.aggregations.splice(
				this.aggregations.indexOf(aggregation), 1)
		})

		console.dir('Aggregations after removing matches')
		console.dir(this.aggregations)

		_.forEach(differ.added, addedMatch => {
			const aggregation =
				aggregationsByAggregationId[addedMatch.aggregationId]
			if (!_.isNil(aggregation)) return

			this.aggregations.push(addedMatch)
		})

		console.dir('Aggregations after adding matches')
		console.dir(this.aggregations)

		_.forEach(differ.modified, changedMatch => {
			const aggregation =
				aggregationsByAggregationId[changedMatch.aggregationId]
			if (_.isNil(aggregation)) return

			// if the match is still fully specified, update the aggregation
			if (this.isReferenceFullySpecified(changedMatch)) {
				_.extend(aggregation, changedMatch)
				return
			}

			// if the match is no longer fully specified, remove the aggregation
			this.aggregations.splice(
				this.aggregations.indexOf(aggregation), 1)

		})

		console.dir('Aggregations after changing matches')
		console.dir(this.aggregations)
	},

	updatePreviousMatches: function() {
		this._previousMatches = _.clone(this.equationEditor._matches)
	},

	updateMatchesAndAggregationIndexes: function() {
		this.updatePreviousMatches()
		this.updateAggregationIndexes()
	},

	updateAggregationIndexes: function() {
		if (_.isNil(this.equationEditor?._matches) ||
			_.isNil(this.equationEditor?._matchIndex)) {
			this.currentMatchAggregationIndex = -1
			return
		}

		const match = this.equationEditor?._matches[this.equationEditor?._matchIndex]
		const referenceForMatch = this.getReference(match)

		const referencesNeedingAggregation =
			this._getReferencesNeedingAggregation()

		// #57623 _.findIndex where we only compare the fieldId and index
		const indexBeforeUpdate = this.currentMatchAggregationIndex
		this.currentMatchAggregationIndex =
			_.findIndex(referencesNeedingAggregation, reference =>
				_.isEqual(_.pick(reference, ['fieldId', 'index']),
					_.pick(referenceForMatch, ['fieldId', 'index'])))
		// aggregation index is changing so update the previous one
		if (indexBeforeUpdate !== this.currentMatchAggregationIndex) {
			this.previousMatchAggregationIndex = this.currentMatchAggregationIndex
		}
	},

	getCurrentMatchAggregation: function() {
		return this.aggregations[this.currentMatchAggregationIndex]
	},

	/**
	 * Returns the {@link EquationEditor._matches} that refer to a field from
	 * a linked dataset, a calculated field, or a manual field. See
	 * {@link FieldEquationValidator#needsAggregation}
	 * @returns {Array<FieldEquationFormReferenceData | {}>}
	 */
	_getMatchesNeedingAggregation: function(matches = this.equationEditor?._matches) {
		return _.values(this._getMatchesNeedingAggregationByIndex(matches))
	},

	/**
	 * @param matches {Array<FieldEquationFormReferenceData | {}>} - the matches to check
	 * @returns {{
	 *     number: FieldEquationFormReferenceData | {}
	 * }}
	 */
	_getMatchesNeedingAggregationByIndex: function(matches = this.equationEditor?._matches) {
		const matchesNeedingAggregationByIndex = {}
		_.forEach(matches, (match, i) => {
			const referenceData = this.getReference(match)
			const needsAggregation = this.needsAggregation(referenceData)
			if (!needsAggregation) return

			matchesNeedingAggregationByIndex[i] = match
		})
		return matchesNeedingAggregationByIndex
	},

	/**
	 * Returns the {@link FieldEquationFormReferenceData} that refer to a field from
	 * a linked dataset, a calculated field, or a manual field. See
	 * {@link FieldEquationValidator#needsAggregation}
	 * @returns {FieldEquationFormReferenceData[]}
	 */
	_getReferencesNeedingAggregation: function(matches = this.equationEditor?._matches) {
		return _.map(this._getMatchesNeedingAggregation(matches), match =>
			this.getReference(match))
	},

	/**
	 * Returns a Differ object that compares the previous matches with the current
	 * @returns {Differ<FieldEquationFormReferenceData|Object>}
	 */
	getDiffOfMatchesNeedingAggregation: function() {
		const propertiesToDiff = ['datasetId', 'fieldId', 'aggregationId',
			'columnOrField', 'field', 'dataset']
		const previous =
				this._getReferencesNeedingAggregation(this._previousMatches)
		const current =
			this._getReferencesNeedingAggregation(this.equationEditor.getMatches())
		const equality = (match1, match2) => {
			match1 = _.pick(match1, propertiesToDiff)
			match2 = _.pick(match2, propertiesToDiff)
			return _.isEqual(match1, match2)
		}
		const identifier = match => match.aggregationId
		return new Differ(previous, current, equality, identifier, identifier)
	},

	getSelectedMatch: function() {
		const allReferences = this.equationEditor.$('.reference')
		const selectedReference = this.equationEditor.$('.reference.is-selected')
		const index = allReferences.index(selectedReference)
		return this.equationEditor?.getMatches()?.[index]
	},

	/**
	 * Returns the label for the given field.  Or if the fieldId == TOTAL,
	 * returns a name like "Total (tracked)".  If the fieldId wasn't specified,
	 * returns undefined.
	 * @param fieldId the ID of the field (could also be TOTAL)
	 * @param [datasetId] when specifying fieldId=TOTAL, disambiguates which
	 * total field we're talking about
	 */
	getFieldLabel: function(fieldId, datasetId) {
		if (fieldId == TOTAL) {
			if (_.isNil(datasetId)) {
				datasetId = this.getDatasetId()
			}
			let dataset = unwrapModel(this.datasetsById[datasetId])
			return i18n.get('datasets.total.tracked', dataset.tracked)
		} else if (!_.isNil(fieldId)) {
			let field = this.fieldsById[fieldId]
			return _.get(field, 'label')
		}
	},

	// toggles whether the add button should be enabled based on the
	// the state of the form controls (e.g. has a valid metric been chosen?)
	toggleAddEnabled: function() {
		this.$('button.add').toggleClass('is-disabled', !this.shouldEnableAddButton())
		// #18958 disable add button if there are linked datasets in the equation
		// this must be done here because this method is called indirectly
		// within process.nextTick by the FormView
		if (!this.linkedDatasetsSupported() &&
			!_.isEmpty(this.getFromModel('linkedDatasets')) &&
			this._hasLinkedDataset(this.equationEditor.getMatches())) {
			this.$('button.add').prop('disabled', true)
		}
	},

	shouldEnableAddButton: function() {
		const sourceDefinition = this.getSourceDefinition()
			let enabled = this.isReferenceFullySpecified(sourceDefinition)
		if (this.allowAnyDataset() && !this.showNativeColumns()) {
			const dataset = this.datasetsById[sourceDefinition?.datasetId]
			const fields = dataset?.get('fields')
			enabled = !_.isNil(fields)
		}
		return enabled
	},

	/**
	 * Gets the aggregations that require a path to the dataset specified in the aggregation.
	 */
	getAggregationsNeedingPaths: function() {
		let aggregationsNeedingPaths = []
		_.forEach(this.aggregations, agg => {
			if (!agg.fieldId) {
				return
			}

			let datasetId = agg.datasetId
			// if the aggregation didn't have a dataset id, then it's the current dataset
			if (!datasetId) {
				const rootDataset = this.getFromModel('rootDataset')
				const field = this.model.combinedFieldsById[agg.fieldId]
				datasetId = field ? field.datasetId : rootDataset.id
			}

			const needsPath = this.shouldShowPathToDataset(datasetId)
			if (!agg.path && needsPath) {
				aggregationsNeedingPaths.push(agg)
			}
		})
		return aggregationsNeedingPaths
	},

	/**
	 * Returns true if we should submit, false if there was some error that we should not submit
	 */
	submitGuard: function() {
		console.log(this.equationEditor.getCleanEquation())
		this._removeInvalidAggregations()
		this._fixEmptyAggregations()
		console.dir(this.aggregations)
		const aggregationsNeedingPaths = this.getAggregationsNeedingPaths()
		if (aggregationsNeedingPaths.length) {
			this.formView.errorMessage(i18n.get('datasets.linked.choosePathToDataset.required'))
			return false
		}

		return true
	},

	saveState: function() {
		this.equation = this.$('.equation').text()
	},

	/**
	 * 	when the user only uses the select menu and Add button to build the equation,
	 *	an 'edit' event is never fired, thus the aggregations are never updated
	 */
	_fixEmptyAggregations: function() {
		if (!_.isEmpty(this.aggregations)) {
			return
		}

		this._previousMatches = []
		_.forEach(this.equationEditor._matches, (match, i) => {
			const referenceData = this.getReference(match)
			const needsAggregation = this.needsAggregation(referenceData)
			if (!needsAggregation) return

			this.equationEditor._matchIndex = i
			this._editForMatchThatNeedsAggregations(referenceData)
		})

		_.forEach(this.aggregations, aggregation => {
			if (!_.isNil(aggregation.aggregationType)) return

			// if any of the aggregations don't have an aggregation type, and didn't
			// need one specified by the user, just set it to 'none'
			if (!this.needsSpecifiedAggregationType(aggregation.fieldId)) {
				aggregation.aggregationType = 'none'
			}
		})

		console.dir(this.aggregations)
	},

	_removeInvalidAggregations: function() {
		this.aggregations = _.filter(this.aggregations, aggregation => !_.isNil(aggregation.fieldId))
	},

	removeDatasetSelectorButton: function () {
		_.invoke(this.datasetSelectorButton, 'remove')
		delete this.datasetSelectorButton
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.fieldDropdown, this.filterBadges,
			this.datasetMarkedField, this.pathToDatasetButton,
			this.dataSourceLink], 'remove')
		this.removeDatasetSelectorButton()
	}

}, {
	getColumnOptions: function(automaticFields, fieldOptions, selectedColumn) {
		let fieldComparisonStrs = _.map(fieldOptions, fieldOption =>
			comparisonStrings.get(fieldOption.label))

		// #55196 use the field's label so that we retain any casing that
		// came from the source data, but all disallowed characters like
		// brackets are removed. We can't use
		// automaticField.intermediateColumnName, because it's all lowercase
		const automaticFieldLabels = automaticFields.map(f => f.label)
		let options = _(automaticFieldLabels)
			.sortBy(column => column.toLowerCase())
			.filter(column => {
				let columnComparisonStr = comparisonStrings.get(column)
				return !_.includes(fieldComparisonStrs, columnComparisonStr)
			})
			.map(column => {
				return {
					label: column,
					value: column,
					selected: column === selectedColumn,
					group: i18n.get('datasets.column.fromSourceData')
				}
			})
			.value()
		return options
	}
})

export default FieldEquationForm

FieldEquationForm.__filename__ = 'datasets/field-equation-form.js'
